/**
 * Service fee — keeps a single, fixed-quantity fee line in sync with the cart.
 *
 * Rules:
 *   - cart holds other products but no fee  -> add one
 *   - cart holds only the fee               -> remove it
 *   - fee quantity drifted from 1           -> reset it
 *
 * The theme hides the quantity input and the remove button on the fee line, but
 * /cart/change.js stays publicly reachable, so this is "hide and repair", not a
 * guarantee. Anything that slips past the UI is corrected on the next sync.
 *
 * Re-rendering is split in two:
 *   - publishing `cartUpdate` lets Dawn redraw the line items (and, in the
 *     drawer, its footer). Our own subscriber ignores our source, so the two
 *     cannot chase each other.
 *   - on the cart page Dawn's onCartUpdate() refreshes `main-cart-items` only;
 *     the totals footer and the `is-empty` class belong to its quantity-change
 *     path, which we never go through. We render those ourselves.
 *
 * Sections are requested on the mutation itself. GET /cart.js silently ignores
 * a `sections` parameter — only the mutation endpoints and the Section
 * Rendering API return rendered markup.
 */
(function () {
  const config = window.serviceFee || {};
  const VARIANT_ID = Number(config.variantId);

  if (!VARIANT_ID) return; // no fee configured in theme settings

  const SOURCE = 'service-fee';
  const DEBOUNCE_MS = 300;
  const BUBBLE_SECTION = 'cart-icon-bubble';

  let syncing = false;
  let syncQueued = false;
  let debounceTimer;

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const footerEl = () => document.getElementById('main-cart-footer');

  function sectionsParam() {
    const ids = [BUBBLE_SECTION];
    const footer = footerEl();
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    return ids.join(',');
  }

  function mutate(path, body) {
    return fetch(`${cartUrl}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionsParam() }),
    }).then((r) => (r.ok ? r.json() : null));
  }

  function replaceInner(target, markup, selector) {
    if (!target || !markup) return;
    const source = new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);
    if (source) target.innerHTML = source.innerHTML;
  }

  function render(response) {
    const footer = footerEl();
    const sections = response && response.sections;

    // /cart/add.js returns only the added line, so it carries no item_count;
    // having just added something, the cart cannot be empty.
    const isEmpty = typeof response?.item_count === 'number' ? response.item_count === 0 : false;

    // The empty state lives on the section wrappers, not in the markup that gets
    // swapped, so it has to be toggled separately — same as Dawn does.
    document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
    footer?.classList.toggle('is-empty', isEmpty);

    if (sections) {
      if (footer) replaceInner(footer.querySelector('.js-contents'), sections[footer.dataset.id], '.js-contents');
      replaceInner(document.getElementById('cart-icon-bubble'), sections[BUBBLE_SECTION], '.shopify-section');
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }
  }

  function sync() {
    if (syncing) {
      syncQueued = true;
      return Promise.resolve();
    }
    syncing = true;

    return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;

        const fee = cart.items.find((i) => i.variant_id === VARIANT_ID);
        const others = cart.items.filter((i) => i.variant_id !== VARIANT_ID);

        if (others.length > 0 && !fee) {
          return mutate('add.js', { items: [{ id: VARIANT_ID, quantity: 1 }] }).then(render);
        }
        if (others.length === 0 && fee) {
          return mutate('change.js', { id: String(VARIANT_ID), quantity: 0 }).then(render);
        }
        // Self-healing: a double add, or someone editing the quantity directly.
        if (fee && fee.quantity !== 1) {
          return mutate('change.js', { id: String(VARIANT_ID), quantity: 1 }).then(render);
        }
      })
      .catch((error) => {
        console.error('[service-fee]', error);
      })
      .then(() => {
        syncing = false;
        if (syncQueued) {
          syncQueued = false;
          return sync();
        }
      });
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, DEBOUNCE_MS);
  }

  function init() {
    sync();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        scheduleSync();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
